import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight } from 'lucide-react';
import { autocomplete } from '../api';

export default function SearchBar({ placeholder, onSearch }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (query.length < 2) {
      setSuggestions(null);
      setOpen(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await autocomplete(query);
        setSuggestions(res.data);
        setOpen(true);
      } catch {
        setSuggestions(null);
      } finally {
        setLoading(false);
      }
    }, 200);
  }, [query]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/attivita?q=${encodeURIComponent(query.trim())}`);
      setOpen(false);
      if (onSearch) onSearch(query.trim());
    }
  };

  const hasSuggestions = suggestions && (
    suggestions.attivita?.length > 0 ||
    suggestions.categorie?.length > 0 ||
    suggestions.argomenti?.length > 0 ||
    suggestions.luoghi?.length > 0
  );

  return (
    <div ref={containerRef} className="relative w-full">
      <form onSubmit={handleSubmit} data-testid="hero-search-input">
        <div className="relative">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ color: 'var(--mi-text)' }}
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query.length >= 2 && setOpen(true)}
            placeholder={placeholder || 'Cerca attività, professionisti, categorie o parole chiave...'}
            className="w-full h-14 pl-11 pr-32 rounded-xl border text-base"
            style={{
              borderColor: 'var(--mi-border)',
              backgroundColor: '#fff',
              color: 'var(--mi-fg)',
              outline: 'none',
              boxShadow: '0 4px 20px rgba(26,26,26,0.06)',
            }}
          />
          <button
            type="submit"
            data-testid="hero-search-submit-button"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-10 px-4 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors"
            style={{ backgroundColor: 'var(--mi-fg)', color: 'var(--mi-bg)' }}
          >
            Cerca <ArrowRight size={14} />
          </button>
        </div>
      </form>

      {/* Autocomplete panel */}
      {open && hasSuggestions && (
        <div
          data-testid="hero-search-suggestions"
          className="absolute left-0 right-0 mt-2 rounded-xl border shadow-xl z-50 overflow-hidden animate-fade-in-up"
          style={{ backgroundColor: '#fff', borderColor: 'var(--mi-border)' }}
        >
          <div className="p-3">
            {/* Attività */}
            {suggestions.attivita?.length > 0 && (
              <div className="mb-3" data-testid="autocomplete-group-attivita">
                <p className="text-xs uppercase tracking-widest px-2 mb-2" style={{ color: 'var(--mi-text)' }}>Attività</p>
                {suggestions.attivita.map((b) => (
                  <button
                    key={b.slug}
                    onClick={() => { navigate(`/attivita/${b.slug}`); setOpen(false); setQuery(''); }}
                    className="flex items-center gap-3 w-full px-2 py-2 rounded-lg text-left hover:bg-gray-50 transition-colors"
                  >
                    {b.logo ? (
                      <img src={b.logo} alt={b.nome} className="w-8 h-8 rounded-full object-contain border p-0.5" style={{ borderColor: 'var(--mi-border)' }} />
                    ) : (
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-display" style={{ backgroundColor: 'var(--mi-muted)' }}>
                        {b.nome?.[0]}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--mi-fg)' }}>{b.nome}</p>
                      <p className="text-xs" style={{ color: 'var(--mi-text)' }}>{b.categoria}{b.citta ? ` — ${b.citta}` : ''}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {/* Categorie */}
            {suggestions.categorie?.length > 0 && (
              <div className="mb-3" data-testid="autocomplete-group-categorie">
                <p className="text-xs uppercase tracking-widest px-2 mb-2" style={{ color: 'var(--mi-text)' }}>Categorie</p>
                {suggestions.categorie.map((c) => (
                  <button
                    key={c.slug}
                    onClick={() => { navigate(`/categoria/${c.slug}`); setOpen(false); setQuery(''); }}
                    className="flex items-center gap-2 w-full px-2 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm" style={{ color: 'var(--mi-fg)' }}>{c.nome}</span>
                    <span className="text-xs ml-auto" style={{ color: 'var(--mi-text)' }}>{c.count} attività</span>
                  </button>
                ))}
              </div>
            )}
            {/* Argomenti */}
            {suggestions.argomenti?.length > 0 && (
              <div className="mb-3" data-testid="autocomplete-group-argomenti">
                <p className="text-xs uppercase tracking-widest px-2 mb-2" style={{ color: 'var(--mi-text)' }}>Argomenti</p>
                {suggestions.argomenti.map((t) => (
                  <button
                    key={t.slug}
                    onClick={() => { navigate(`/argomento/${t.slug}`); setOpen(false); setQuery(''); }}
                    className="flex items-center gap-2 w-full px-2 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--mi-muted)', color: 'var(--mi-text)' }}>{t.nome}</span>
                  </button>
                ))}
              </div>
            )}
            {/* Luoghi */}
            {suggestions.luoghi?.length > 0 && (
              <div className="mb-2">
                <p className="text-xs uppercase tracking-widest px-2 mb-2" style={{ color: 'var(--mi-text)' }}>Luoghi</p>
                {suggestions.luoghi.map((l) => (
                  <button
                    key={l.nome}
                    onClick={() => { navigate(`/attivita?citta=${encodeURIComponent(l.nome)}`); setOpen(false); setQuery(''); }}
                    className="flex items-center gap-2 w-full px-2 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm" style={{ color: 'var(--mi-fg)' }}>{l.nome}</span>
                    {l.provincia && <span className="text-xs" style={{ color: 'var(--mi-text)' }}>({l.provincia})</span>}
                  </button>
                ))}
              </div>
            )}
            <div className="border-t pt-2 px-2" style={{ borderColor: 'var(--mi-border)' }}>
              <button
                onClick={() => { navigate(`/attivita?q=${encodeURIComponent(query)}`); setOpen(false); }}
                className="text-xs font-medium hover:underline"
                style={{ color: 'var(--mi-accent)' }}
              >
                Vedi tutti i risultati per “{query}” →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No results */}
      {open && !hasSuggestions && query.length >= 2 && !loading && (
        <div
          className="absolute left-0 right-0 mt-2 rounded-xl border shadow-xl z-50 p-4 text-center"
          style={{ backgroundColor: '#fff', borderColor: 'var(--mi-border)' }}
        >
          <p className="text-sm" style={{ color: 'var(--mi-text)' }}>Nessun suggerimento. Premi Invio per cercare comunque.</p>
        </div>
      )}
    </div>
  );
}
